import {Injectable} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {WeeklyScheduleDTO} from '../api/api';

interface Overlap {
  days: number[];
  ranges: TimeRange[];
}

type TimeRange = [number, number, number, number];

enum WeekDays {
  Mon = 'mon',
  Tue = 'tue',
  Wed = 'wed',
  Thu = 'thu',
  Fri = 'fri',
  Sat = 'sat',
  Sun = 'sun'
}

const RANGE_DAYS = {
  FULL: [0, 6],
  WEEKDAY: [0, 4],
  WORKING_DAYS: [0, 5],
  WEEKEND: [5, 6]
};

@Injectable({
  providedIn: 'root'
})
export class WeeklyScheduleService {

  public readonly dayKeys: string[];

  constructor(
    private translate: TranslateService
  ) {
    this.dayKeys = Object.values(WeekDays);
  }

  toString(cycle: WeeklyScheduleDTO): string {
    if (!cycle) {
      return this.translate.instant('lang.weeklySchedule.noRanges');
    }

    const overlaps: Overlap[] = this._getOverlaps(cycle);
    if (overlaps && overlaps.length > 0) {
      return overlaps.map(overlap => this._formatOverlap(overlap)).join('; ');
    }
    return this.translate.instant('lang.weeklySchedule.noRanges');
  }

  private _getOverlaps(cycle: WeeklyScheduleDTO): Overlap[] {
    const overlaps: Overlap[] = [];

    this.dayKeys.forEach((key, dayIndex) => {
      const day = cycle[key];
      if (day?.intervals) {
        const intervals: TimeRange[] =
          day.intervals
            .map(r => [r.fromHour, r.fromMinute, r.tillHour, r.tillMinute])
            .sort((a, b) => (a[0] * 60 + a[1]) - (b[0] * 60 + b[1]));

        // Search for existing range
        const index = overlaps.findIndex(r => JSON.stringify(r.ranges) === JSON.stringify(intervals));

        if (index > -1) {
          // Add to found range
          overlaps[index].days.push(dayIndex);
        } else {
          // Create new range
          overlaps.push({ranges: intervals, days: [dayIndex]});
        }
      }
    });

    return overlaps;
  }

  private _formatOverlap(overlap: Overlap): string {
    const dayNames: string[] = this.getDayNames(overlap.days);
    const formattedRanges: string[] = overlap.ranges.map(rTime => this.formatRangeTime(rTime));
    return dayNames.join(', ') + ' ' + formattedRanges.join(', ');
  }

  private formatRangeTime(timeRange: number[]): string {
    if (timeRange.length > 1 && timeRange[0] < 60 && timeRange[1] < 60) {
      const formatTime = (i: number) => ('00' + timeRange[i]).slice(-2) + ':' + ('00' + timeRange[i + 1]).slice(-2);
      return this.translate.instant('lang.weeklySchedule.range.singleTime', {
        from: timeRange[1] > 0 ? formatTime(0) : timeRange[0].toString(),
        till: timeRange[3] > 0 ? formatTime(2) : timeRange[2].toString()
      });
    }
  }

  private getDayNameByRange(range: number[]): string {
    const [startRange, endRange] = range;
    switch (range.toString()) {
      case RANGE_DAYS.FULL.toString():
        return this.translate.instant('lang.weeklySchedule.range.daily');

      case RANGE_DAYS.WORKING_DAYS.toString():
        return this.translate.instant('lang.weeklySchedule.range.workingDays');

      case RANGE_DAYS.WEEKEND.toString():
        return this.translate.instant('lang.weeklySchedule.range.weekend');

      case RANGE_DAYS.WEEKDAY.toString():
        return this.translate.instant('lang.weeklySchedule.range.weekdays');

      default:
        const startDayName = this.translate.instant('lang.globals.weekday.' + this.dayKeys[startRange]);
        const endDayName = this.translate.instant('lang.globals.weekday.' + this.dayKeys[endRange]);
        return endRange - startRange > 1
          ? `${startDayName} ${this.translate.instant('lang.globals.till')} ${endDayName}`
          : `${startDayName}, ${endDayName}`;
    }
  }

  private getDayNames(days: number[]): string[] {
    const dayNames: string[] = [];
    let startRange: number = days[0];
    for (let i: number = 1; i <= days.length; i++) {
      if (i === days.length || days[i] - days[i - 1] !== 1) {
        const endRange: number = days[i - 1];
        const range: number[] = [startRange, endRange];

        dayNames.push(
          startRange === endRange
            ? this.translate.instant('lang.globals.weekday.' + this.dayKeys[startRange])
            : this.getDayNameByRange(range)
        );

        if (i < days.length) {
          startRange = days[i];
        }
      }
    }
    return dayNames;
  }

}
